/**
 * Chat History Service
 * Manages local storage of chat questions and answers
 */

const STORAGE_KEY = 'rubiq_chat_history';
const MAX_HISTORY_ITEMS = 100; // Limit to prevent storage overflow

/**
 * Get all chat history from local storage
 * @returns {Array} Array of history items
 */
export const getChatHistory = () => {
  try {
    const history = localStorage.getItem(STORAGE_KEY);
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('Error reading chat history:', error);
    return [];
  }
};

/**
 * Save a new chat interaction to history
 * @param {Object} item - The chat item to save
 * @param {string} item.question - The user's question
 * @param {string} item.answer - The bot's answer
 * @param {string} item.deviceSerial - Optional device serial number
 * @param {string} item.category - Optional category/type of question
 * @returns {boolean} Success status
 */
export const saveChatHistoryItem = (item) => {
  try {
    const history = getChatHistory();
    
    const newItem = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      question: item.question,
      answer: item.answer,
      deviceSerial: item.deviceSerial || null,
      category: item.category || 'general',
      timestamp: new Date().toISOString(),
      conversationId: item.conversationId || null,
    };

    // Add to beginning of array (most recent first)
    history.unshift(newItem);

    // Limit history size
    const trimmedHistory = history.slice(0, MAX_HISTORY_ITEMS);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedHistory));
    return true;
  } catch (error) {
    console.error('Error saving chat history:', error);
    return false;
  }
};

/**
 * Delete a specific history item
 * @param {string} id - The ID of the item to delete
 * @returns {boolean} Success status
 */
export const deleteChatHistoryItem = (id) => {
  try {
    const history = getChatHistory();
    const filteredHistory = history.filter(item => item.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredHistory));
    return true;
  } catch (error) {
    console.error('Error deleting chat history item:', error);
    return false;
  }
};

/**
 * Clear all chat history
 * @returns {boolean} Success status
 */
export const clearChatHistory = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing chat history:', error);
    return false;
  }
};

/**
 * Search chat history
 * @param {string} searchTerm - The term to search for
 * @returns {Array} Filtered history items
 */
export const searchChatHistory = (searchTerm) => {
  const history = getChatHistory();
  if (!searchTerm) return history;

  const term = searchTerm.toLowerCase();
  return history.filter(item => 
    item.question.toLowerCase().includes(term) ||
    item.answer.toLowerCase().includes(term) ||
    (item.deviceSerial && item.deviceSerial.toLowerCase().includes(term)) ||
    (item.category && item.category.toLowerCase().includes(term))
  );
};

/**
 * Filter history by date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Array} Filtered history items
 */
export const filterHistoryByDate = (startDate, endDate) => {
  const history = getChatHistory();
  return history.filter(item => {
    const itemDate = new Date(item.timestamp);
    return itemDate >= startDate && itemDate <= endDate;
  });
};

/**
 * Filter history by category
 * @param {string} category - The category to filter by
 * @returns {Array} Filtered history items
 */
export const filterHistoryByCategory = (category) => {
  const history = getChatHistory();
  if (!category || category === 'all') return history;
  return history.filter(item => item.category === category);
};

/**
 * Filter history by device serial
 * @param {string} deviceSerial - The device serial to filter by
 * @returns {Array} Filtered history items
 */
export const filterHistoryByDevice = (deviceSerial) => {
  const history = getChatHistory();
  if (!deviceSerial) return history;
  return history.filter(item => item.deviceSerial === deviceSerial);
};

/**
 * Get statistics about chat history
 * @returns {Object} Statistics object
 */
export const getChatHistoryStats = () => {
  const history = getChatHistory();
  
  const stats = {
    totalQuestions: history.length,
    categories: {},
    devices: {},
    dateRange: {
      oldest: null,
      newest: null,
    }
  };

  if (history.length === 0) return stats;

  // Count by category
  history.forEach(item => {
    stats.categories[item.category] = (stats.categories[item.category] || 0) + 1;
    if (item.deviceSerial) {
      stats.devices[item.deviceSerial] = (stats.devices[item.deviceSerial] || 0) + 1;
    }
  });

  // Get date range
  const timestamps = history.map(item => new Date(item.timestamp));
  stats.dateRange.oldest = new Date(Math.min(...timestamps));
  stats.dateRange.newest = new Date(Math.max(...timestamps));

  return stats;
};

/**
 * Export history to JSON file
 * @returns {string} JSON string of history
 */
export const exportChatHistory = () => {
  const history = getChatHistory();
  return JSON.stringify(history, null, 2);
};

/**
 * Import history from JSON
 * @param {string} jsonString - JSON string to import
 * @returns {boolean} Success status
 */
export const importChatHistory = (jsonString) => {
  try {
    const importedHistory = JSON.parse(jsonString);
    if (!Array.isArray(importedHistory)) {
      throw new Error('Invalid history format');
    }
    
    // Merge with existing history, keeping unique items
    const existingHistory = getChatHistory();
    const mergedHistory = [...importedHistory, ...existingHistory];
    
    // Remove duplicates based on timestamp and question
    const uniqueHistory = mergedHistory.reduce((acc, current) => {
      const duplicate = acc.find(item => 
        item.timestamp === current.timestamp && 
        item.question === current.question
      );
      if (!duplicate) {
        acc.push(current);
      }
      return acc;
    }, []);

    // Sort by timestamp (newest first)
    uniqueHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    // Limit size
    const trimmedHistory = uniqueHistory.slice(0, MAX_HISTORY_ITEMS);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedHistory));
    return true;
  } catch (error) {
    console.error('Error importing chat history:', error);
    return false;
  }
};
